import { Address, Service } from './index';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { getParams, sellerApiClient } from './sellerApiClient';
import { PaginationModel } from '@hooks/usePagingSearchParams';
import { useQuery } from 'react-query';

const defaultParams: PaginationModel = { page: 1, pageSize: 1 };

export type TRequestParams = {
  filters?: FilterModel[];
  page: PaginationModel;
};

export type TReexportTariffParams = {
  countryCode: string;
  id: number;
  marketplaceType: string;
  price: {
    amount: number;
    currency: string;
  };
};

export type TReexportTariffsParams = {
  reexportTariffs: TReexportTariffParams[];
};

export const getReexportTariffsData = async (): Promise<TReexportTariffsParams> => {
  const response = await sellerApiClient.get<TReexportTariffsParams>('/v1/problematicparcels/reexport-tariffs');
  return response.data;
};

export type TProblematicFirstMileParcelItem = {
  id: string;
  name: string;
  quantity: number;
  url: string;
  vendorCode: string;
  declaredValue?: {
    amount: number;
    currency: string;
  }
};

export type TProblematicFirstMileParcel = {
  countryCode: string;
  createAt: Date;
  declaredValue?: {
    amount: number;
    currency: string;
  };
  decisionWasMadeAt: Date;
  estimatedUtilizationDate: Date;
  externalId: string;
  id: string;
  isCancelable: boolean;
  isExtraSmall: boolean;
  items: TProblematicFirstMileParcelItem[];
  marketplaceType: string;
  reason: string;
  sellerId: string;
  sellerName: string;
  serviceName: string;
  status: string;
  trackNumber: string;
  weight: {
    unit: string;
    value: number;
  };
};

export type TProblematicFirstMileParcelsResponse = {
  total: number;
  items: TProblematicFirstMileParcel[];
};

export const getProblematicFirstMileParcels = async (params: TRequestParams): Promise<TProblematicFirstMileParcelsResponse> => {
  const { filters, page } = params;
  const response = await sellerApiClient.get<TProblematicFirstMileParcelsResponse>('/v1/problematicparcels/first-mile/page', {
    params: getParams(page, filters),
    paramsSerializer: { indexes: null }
  });
  return response.data;
};

export const useProblematicFirstMileCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ['getProblematicFirstMileParcels', defaultParams],
    queryFn: () => getProblematicFirstMileParcels({
      filters: [],
      page: defaultParams
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled
  });
  return { data, refetch };
};

export type TProblematicCustomsParcelItem = {
  declaredValue?: {
    amount: number;
    currency: string;
  };
  id: string;
  name: string;
  quantity: number;
  url: string;
  vendorCode: string;
};

export type TProblematicCustomsParcel = {
  countryCode: string;
  createAt: Date;
  declaredValue?: {
    amount: number;
    currency: string;
  };
  decisionWasMadeAt: Date;
  estimatedUtilizationDate: Date;
  externalId: string;
  id: string;
  isCancelable: boolean;
  isExtraSmall: boolean;
  items: TProblematicCustomsParcelItem[];
  marketplaceType: string;
  reason: string;
  reexportPrice: {
    amount: number;
    currency: string;
  };
  sellerId: string;
  sellerName: string;
  serviceName: string;
  status: string;
  trackNumber: string;
  weight: {
    unit: string;
    value: number;
  };
};

export type TProblematicCustomsParcelsResponse = {
  total: number;
  items: TProblematicCustomsParcel[];
};

export const getProblematicCustomsParcels = async (params: TRequestParams): Promise<TProblematicCustomsParcelsResponse> => {
  const { filters, page } = params;
  const response = await sellerApiClient.get<TProblematicCustomsParcelsResponse>('/v1/problematicparcels/customs/page', {
    params: getParams(page, filters),
    paramsSerializer: { indexes: null }
  });
  return response.data;
};

export const useProblematicCustomsCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ['getProblematicCustomsParcels', defaultParams],
    queryFn: () => getProblematicCustomsParcels({
      filters: [],
      page: defaultParams
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled
  });
  return { data, refetch };
};

export type TProblematicOnWayParcelItem = {
  id: string;
  name: string;
  quantity: number;
  url: string;
  vendorCode: string;
  declaredValue?: {
    amount: number;
    currency: string;
  }
};

export type TProblematicOnWayParcel = {
  actionPrice: {
    amount: number;
    currency: string;
  };
  countryCode: string;
  createAt: Date;
  declaredValue?: {
    amount: number;
    currency: string;
  };
  decisionWasMadeAt: Date;
  estimatedUtilizationDate: Date;
  externalId: string;
  id: string;
  isCancelable: boolean;
  isExtraSmall: boolean;
  items: TProblematicOnWayParcelItem[];
  marketplaceType: string;
  reason: string;
  sellerId: string;
  sellerName: string;
  serviceName: string;
  status: string;
  trackNumber: string;
  weight: {
    unit: string;
    value: number;
  };
};

export type TProblematicOnWayParcelsResponse = {
  total: number;
  items: TProblematicOnWayParcel[];
};

export const getProblematicOnWayParcels = async (params: TRequestParams): Promise<TProblematicOnWayParcelsResponse> => {
  const { filters, page } = params;
  const response = await sellerApiClient.get<TProblematicOnWayParcelsResponse>('/v1/problematicparcels/on-way/page', {
    params: getParams(page, filters),
    paramsSerializer: { indexes: null }
  });
  return response.data;
};

export const useProblematicOnWayCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ['getProblematicOnWayParcels', defaultParams],
    queryFn: () => getProblematicOnWayParcels({
      filters: [],
      page: defaultParams
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled
  });
  return { data, refetch };
};

export type TProblematicDisposalParcelItem = {
  id: string;
  name: string;
  quantity: number;
  url: string;
  vendorCode: string;
  declaredValue?: {
    amount: number;
    currency: string;
  }
};

export type TProblematicDisposalParcel = {
  countryCode: string;
  createAt: Date;
  declaredValue?: {
    amount: number;
    currency: string;
  };
  decisionWasMadeAt: Date;
  estimatedUtilizationDate: Date;
  externalId: string;
  id: string;
  isCancelable: boolean;
  isExtraSmall: boolean;
  items: TProblematicDisposalParcelItem[];
  changeInitiator: string;
  marketplaceType: string;
  reason: string;
  sellerId: string;
  sellerName: string;
  serviceName: string;
  status: string;
  trackNumber: string;
  weight: {
    unit: string;
    value: number;
  };
};

export type TProblematicDisposalParcelsResponse = {
  total: number;
  items: TProblematicDisposalParcel[];
};

export const getProblematicDisposalParcels = async (params: TRequestParams): Promise<TProblematicDisposalParcelsResponse> => {
  const { filters, page } = params;

  const filtersPrepared = (filters || []).map(f => {
    if (f.name === "changeInitiator") {
      return {
        ...f,
        value: (Array.isArray(f.value) ? f.value : [f.value]).map((v: string) => (
          (v === "Seller") ? ["SellerDashboard", "WeChat"] : ["Unknown", "Auto"]
        )).reduce((acc: string[], item: string[]) => ([...acc, ...item]), []),
      }
    }
    return f;
  })

  const response = await sellerApiClient.get<TProblematicDisposalParcelsResponse>('/v1/problematicparcels/dispose/page', {
    params: getParams(page, filtersPrepared),
    paramsSerializer: { indexes: null }
  });
  return response.data;
};

export const useProblematicDisposalCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ['getProblematicDisposalParcels', defaultParams],
    queryFn: () => getProblematicDisposalParcels({
      filters: [],
      page: defaultParams
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled
  });
  return { data, refetch };
};

export type TDisposalOrderParams = {
  parcelIds: string[];
};

export const sendDisposalOrder = async (params: TDisposalOrderParams): Promise<void> => {
  const response = await sellerApiClient.post<void>('/v1/problematicparcels/dispose', params);
  return response.data;
};

export type TReturnOrderAddress = {
  address: Address | undefined;
  contactInfo: {
    name: string | undefined;
    phone: string | undefined;
  };
};

export type TReturnOrderParams = {
  addresses: TReturnOrderAddress[];
  parcelIds: (string | undefined)[];
};

export type TReturnOrderResponse = {
  orderId: string;
};

export const sendFirstMileReturnOrder = async (params: TReturnOrderParams): Promise<TReturnOrderResponse> => {
  const response = await sellerApiClient.post<TReturnOrderResponse>('/v1/problematicparcels/first-mile/reexport-order/create', params);
  return response.data;
};

export const sendCustomsReturnOrder = async (params: TReturnOrderParams): Promise<TReturnOrderResponse> => {
  const response = await sellerApiClient.post<TReturnOrderResponse>('/v1/problematicparcels/customs/reexport-order/create', params);
  return response.data;
};

export type TCheckReturnOrderStatusResponse = {
  orderStatus: string;
};

export const checkReturnOrderStatus = async (orderId: string): Promise<TCheckReturnOrderStatusResponse> => {
  const response = await sellerApiClient.get(`/v1/problematicparcels/customs/reexport-order/${orderId}/status`);
  return response.data;
};

export const getProblematicServices = async (): Promise<Service[]> => {
  const response = await sellerApiClient.get<{ items: Service[] }>('/v1/problematicparcels/services');
  return response.data?.items;
};

export type TApproachingDeadlineResponse = {
  countCustoms: number;
  countFirstMile: number;
};

export const getApproachingDeadline = async (): Promise<TApproachingDeadlineResponse> => {
  const response = await sellerApiClient.get<TApproachingDeadlineResponse>('/v1/problematicparcels/approaching-deadline');
  return response.data;
};
